import type { StarterItem } from '@/config/reds';
import { isManagerOrAbove } from '@/domain/permissions';
import { SECTION_KIND, type Item, type Kind, type SectionId } from '@/domain/types';
import { rt } from './runtime';
import { DomainError, inTx, newId, newMeta, putAudited, touch, writeAudit } from './write';

/** Every row of a list as printed on the sheet (blank rows too), in order. */
export function sheetRowsOf(items: Item[], section: SectionId, includeInactive = false): Item[] {
  return items.filter((i) => i.rows[section] != null && (includeInactive || i.active)).sort((a, b) => a.rows[section]! - b.rows[section]!);
}

/** The drinks on a list to count, in row order (blank rows left out). */
export function rowsOf(items: Item[], section: SectionId, includeInactive = false): Item[] {
  return sheetRowsOf(items, section, includeInactive).filter((i) => !i.blankRow);
}

export const itemLabel = (i: Pick<Item, 'name' | 'blankRow'>) => (i.blankRow ? 'Blank row' : i.name);

export function blankItem(name: string, kind: Kind, rows: Item['rows']): Omit<Item, keyof ReturnType<typeof newMeta>> {
  return { name, kind, rows, active: true, colB: null, usualFront: null, caseSize: null, parCases: null, unitCost: null, bottleMl: null, drinkPrice: null, servingOz: null };
}

/** Puts in the starting lists (a new tablet, or practice mode). */
export async function createStarterItems(starter: StarterItem[], isSample = false): Promise<void> {
  const db = rt().db;
  if ((await db.items.count()) > 0) throw new DomainError('The lists are already set up.');
  await inTx(['items'], async () => {
    for (const s of starter) {
      const item: Item = { ...newMeta(newId('itm-')), ...blankItem(s.name, s.kind, { ...s.rows }), colB: s.colB ?? null, caseSize: s.caseSize ?? null, parCases: s.parCases ?? null, ...(s.blankRow ? { blankRow: true } : {}), ...(isSample ? { isSample: true } : {}) };
      await db.items.put(item);
    }
  });
}

const cleanName = (n: string) => n.trim().replace(/\s+/g, ' ');

function requireManager() {
  if (!isManagerOrAbove(rt().actor.role)) throw new DomainError('Only a manager can change the drink lists.');
}

async function assertUniqueName(name: string, kind: Kind, exceptId?: string) {
  const clash = (await rt().db.items.toArray()).find((i) => i.kind === kind && i.active && !i.blankRow && i.id !== exceptId && i.name.toLowerCase() === name.toLowerCase());
  if (clash) throw new DomainError(`There’s already a ${kind} called “${clash.name}”.`);
}

/** Adds a drink at the bottom of a list. */
export async function addItem(input: { name: string; section: SectionId }): Promise<Item> {
  requireManager();
  const name = cleanName(input.name);
  if (!name) throw new DomainError('Type the drink’s name as it should appear on the sheet.');
  if (name.length > 40) throw new DomainError('That name is too long for the sheet (40 letters at most).');
  const kind = SECTION_KIND[input.section];
  return inTx(['items'], async () => {
    await assertUniqueName(name, kind);
    const items = await rt().db.items.toArray();
    const last = Math.max(0, ...items.map((i) => i.rows[input.section] ?? 0));
    const item: Item = { ...newMeta(newId('itm-')), ...blankItem(name, kind, { [input.section]: last + 1 }) };
    await putAudited('items', item, null, { action: 'item.add', summary: `Added ${name}` });
    return item;
  });
}

/** Adds an empty row at the bottom of a liquor list (on the sheet, not counted). */
export async function addBlankRow(section: SectionId): Promise<Item> {
  requireManager();
  return inTx(['items'], async () => {
    const items = await rt().db.items.toArray();
    const last = Math.max(0, ...items.map((i) => (i.active ? (i.rows[section] ?? 0) : 0)));
    const item: Item = { ...newMeta(newId('itm-')), ...blankItem('', SECTION_KIND[section], { [section]: last + 1 }), blankRow: true };
    await putAudited('items', item, null, { action: 'item.add_blank', summary: 'Added a blank row' });
    return item;
  });
}

export type ItemPatch = Partial<Pick<Item, 'name' | 'colB' | 'spoutOz' | 'usualFront' | 'caseSize' | 'parCases' | 'unitCost' | 'bottleMl' | 'drinkPrice' | 'servingOz'>>;

function checkNumber(label: string, v: number | null | undefined, opts: { integer?: boolean; positive?: boolean; max?: number } = {}) {
  if (v == null) return;
  if (!Number.isFinite(v) || v < 0) throw new DomainError(`${label} must be 0 or more.`);
  if (opts.positive && v === 0) throw new DomainError(`${label} must be more than 0.`);
  if (opts.integer && !Number.isInteger(v)) throw new DomainError(`${label} must be a whole number.`);
  if (opts.max != null && v > opts.max) throw new DomainError(`${label} looks too big (more than ${opts.max}).`);
}

export async function saveItem(id: string, patch: ItemPatch): Promise<Item> {
  requireManager();
  checkNumber('The usual amount', patch.usualFront, { integer: true, max: 1000 });
  checkNumber('Bottles in a case', patch.caseSize, { integer: true, positive: true, max: 100 });
  checkNumber('The par', patch.parCases, { max: 1000 });
  checkNumber('The cost', patch.unitCost, { max: 10000 });
  checkNumber('The bottle size', patch.bottleMl, { positive: true, max: 20000 });
  checkNumber('The drink price', patch.drinkPrice, { max: 1000 });
  checkNumber('The pour', patch.servingOz, { positive: true, max: 40 });
  checkNumber('The spout weight', patch.spoutOz, { max: 5 });
  return inTx(['items'], async () => {
    const before = await rt().db.items.get(id);
    if (!before) throw new DomainError('That drink no longer exists.');
    const next = touch<Item>(before, patch);
    if (before.blankRow && patch.name != null && patch.name.trim() !== '') next.blankRow = false;
    if (next.blankRow) next.name = '';
    else if (patch.name != null) {
      next.name = cleanName(patch.name);
      if (!next.name) throw new DomainError('The name can’t be empty.');
      if (next.name.length > 40) throw new DomainError('That name is too long for the sheet (40 letters at most).');
      await assertUniqueName(next.name, next.kind, id);
    }
    if (typeof next.colB === 'string') next.colB = next.colB.trim() || null;
    await putAudited('items', next, before, { action: before.blankRow && !next.blankRow ? 'item.from_blank' : 'item.update', summary: before.blankRow && !next.blankRow ? `Turned the blank row into ${next.name}` : `Changed ${itemLabel(next)}` });
    return next;
  });
}

/** Sets the spout weight on every liquor bottle at once (0 = no spouts). Returns how many. */
export async function setSpoutForAll(oz: number): Promise<number> {
  requireManager();
  checkNumber('The spout weight', oz, { max: 5 });
  return inTx(['items'], async () => {
    const bottles = (await rt().db.items.toArray()).filter((i) => i.kind === 'liquor' && !i.blankRow);
    const spoutOz = oz === 0 ? null : oz;
    let changed = 0;
    for (const b of bottles) {
      if (b.spoutOz === spoutOz) continue;
      await rt().db.items.put(touch(b, { spoutOz }));
      changed++;
    }
    await writeAudit({ action: 'item.spout_all', entity: 'items', entityId: 'all', summary: spoutOz ? `Set the spout weight to ${spoutOz} oz on every liquor bottle` : 'Took the spout weight off every liquor bottle' });
    return changed;
  });
}

/** Moves a drink up (−1) or down (+1) one row on a list. */
export async function moveRow(id: string, section: SectionId, delta: -1 | 1): Promise<void> {
  requireManager();
  await inTx(['items'], async () => {
    const list = sheetRowsOf(await rt().db.items.toArray(), section);
    const i = list.findIndex((x) => x.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    const a = list[i]!;
    const b = list[j]!;
    const ra = a.rows[section]!;
    const rb = b.rows[section]!;
    await putAudited('items', touch(a, { rows: { ...a.rows, [section]: rb } }), a, { action: 'item.move', summary: `Moved ${a.name} ${delta < 0 ? 'up' : 'down'}` });
    await putAudited('items', touch(b, { rows: { ...b.rows, [section]: ra } }), b, { action: 'item.move', summary: `Moved ${b.name} ${delta < 0 ? 'down' : 'up'}` });
  });
}

/** Puts a beer on the other beer list too (at the bottom). */
export async function addToSection(id: string, section: SectionId): Promise<void> {
  requireManager();
  await inTx(['items'], async () => {
    const items = await rt().db.items.toArray();
    const it = items.find((x) => x.id === id);
    if (!it) throw new DomainError('That drink no longer exists.');
    if (SECTION_KIND[section] !== it.kind) throw new DomainError(`${it.name} can’t go on that list.`);
    if (it.rows[section] != null) return;
    const last = Math.max(0, ...items.map((i) => i.rows[section] ?? 0));
    const rows = { ...it.rows, [section]: last + 1 };
    // Liquor is on exactly one of its two lists.
    if (it.kind === 'liquor') for (const s of ['liquor', 'liqueur'] as const) if (s !== section) delete rows[s];
    await putAudited('items', touch(it, { rows, active: true }), it, { action: 'item.add_to_list', summary: `Put ${it.name} on another list` });
    await renumber(section);
  });
}

/**
 * Takes a drink off a list. Off every list = no longer counted (kept, so past weeks still
 * show it). Earlier counts are never deleted.
 */
export async function removeFromSection(id: string, section: SectionId): Promise<void> {
  requireManager();
  await inTx(['items'], async () => {
    const it = await rt().db.items.get(id);
    if (!it) throw new DomainError('That drink no longer exists.');
    const rows = { ...it.rows };
    delete rows[section];
    const active = Object.keys(rows).length > 0;
    await putAudited('items', touch(it, { rows: active ? rows : it.rows, active }), it, { action: active ? 'item.remove_from_list' : 'item.archive', summary: active ? `Took ${it.name} off a list` : `Stopped counting ${it.name}` });
    await renumber(section);
  });
}

/** Brings back a drink that was no longer counted, at the bottom of its lists. */
export async function restoreItem(id: string): Promise<void> {
  requireManager();
  await inTx(['items'], async () => {
    const items = await rt().db.items.toArray();
    const it = items.find((x) => x.id === id);
    if (!it || it.active) return;
    await assertUniqueName(it.name, it.kind, id);
    const rows: Item['rows'] = {};
    for (const s of Object.keys(it.rows) as SectionId[]) rows[s] = Math.max(0, ...items.filter((x) => x.active).map((x) => x.rows[s] ?? 0)) + 1;
    await putAudited('items', touch(it, { rows, active: true }), it, { action: 'item.restore', summary: `Started counting ${it.name} again` });
  });
}

/** Closes gaps so the rows are 1, 2, 3… */
async function renumber(section: SectionId) {
  const list = sheetRowsOf(await rt().db.items.toArray(), section);
  for (let i = 0; i < list.length; i++) {
    const it = list[i]!;
    if (it.rows[section] !== i + 1) await rt().db.items.put(touch(it, { rows: { ...it.rows, [section]: i + 1 } }));
  }
}
